import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid,
  IconButton, Stack, TextField, Tooltip, Button,
  CircularProgress, Alert, Paper, InputAdornment,
  Avatar, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  DirectionsBus as BusIcon,
  People as PeopleIcon, // Added for student count
  Route as RouteIcon, // Added for route icon
  Person as PersonIcon // Added missing icon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBuses, deleteBus, clearDeleteError } from '../slices/busesSlice';
import { useToast } from '../context/ToastContext';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';

// --- Styled Components --- //

const StyledCard = styled(Card)(() => ({
  transition: 'transform 0.25s ease-in-out, box-shadow 0.25s ease-in-out',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 24,
  border: '1px solid #E5E7EB',
  backgroundColor: '#fff',
  boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
  fontFamily: 'Cairo, Tajawal, sans-serif',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(67,56,202,0.14)',
    cursor: 'pointer',
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #EEF2FF 0%, #E0F2FE 100%)',
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  borderBottom: '1px solid #E5E7EB',
}));

const CardActionBar = styled(Box)(({ theme }) => ({
  marginTop: 'auto', // Push actions to the bottom
  padding: theme.spacing(1, 2),
  borderTop: '1px solid #E5E7EB',
  backgroundColor: '#F8F9FF',
}));

const MotionGrid = motion(Grid);
const MotionPaper = motion(Paper);

// --- BusesPage Component --- //

const BusesPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBusId, setSelectedBusId] = useState(null);
  const [selectedBusName, setSelectedBusName] = useState('');
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();

  // Use the filtered list from the slice if filter logic is maintained there
  // Otherwise, filter here based on the main list
  const { list: busList, status, error, deleteError } = useSelector((state) => state.buses);

  useEffect(() => {
   if (status === 'idle') {
      dispatch(fetchBuses());
    }
  }, [status, dispatch]);

  // Handle delete errors
  useEffect(() => {
    if (deleteError) {
      setErrorMessage(deleteError.message || 'حدث خطأ أثناء حذف الحافلة');
      setErrorDialogOpen(true);
    }
  }, [deleteError]);

  const handleBusClick = (id) => {
    navigate(`/buses/view/${id}`);
  };

  const handleAddClick = () => {
    navigate('/buses/create');
  };

  const handleEditClick = (id, e) => {
    e.stopPropagation(); // Prevent card click navigation
    navigate(`/buses/edit/${id}`);
  };

  const handleDeleteClick = (id, busName, e) => {
    e.stopPropagation(); // Prevent card click navigation
    setSelectedBusId(id);
    setSelectedBusName(busName || 'غير المسماة');
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      // Clear any previous delete errors
      dispatch(clearDeleteError());
      
      const result = await dispatch(deleteBus(selectedBusId));
      
      if (deleteBus.fulfilled.match(result)) {
        showToast('تم حذف الحافلة بنجاح', 'success');
        setDeleteDialogOpen(false);
        setSelectedBusId(null);
        setSelectedBusName('');
      }
    } catch (err) {
      console.error('Failed to delete bus:', err);
      // Error will be handled by the useEffect that watches deleteError
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedBusId(null);
    setSelectedBusName('');
  };

  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);
    setDeleteDialogOpen(false);
    dispatch(clearDeleteError());
  };

  // Filtering logic (can be moved to slice/selector for better performance)
  const filteredBuses = busList.filter(
    (bus) =>
      (bus.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (bus.bus_number?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (bus.driver?.full_name?.toLowerCase() || bus.manager_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (bus.bus_type?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  // --- Render Logic --- //

  if (status === 'loading') {
    return (
      <Box
        sx={{
          direction: 'rtl',
          fontFamily: 'Cairo, Tajawal, sans-serif',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          minHeight: '100vh',
          bgcolor: '#F8F9FF',
        }}
      >
        <CircularProgress size={56} sx={{ color: '#4338CA' }} />
        <Typography sx={{ color: '#6B7280', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
          جاري تحميل الحافلات...
        </Typography>
      </Box>
    );
  }

  if (status === 'failed') {
    return (
      <Box
        sx={{
          direction: 'rtl',
          fontFamily: 'Cairo, Tajawal, sans-serif',
          p: { xs: 2, md: 3 },
          minHeight: '100vh',
          bgcolor: '#F8F9FF',
          textAlign: 'center',
        }}
      >
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
          فشل تحميل قائمة الحافلات. يرجى المحاولة مرة أخرى.
          {error && <Typography variant="caption"><br />{error}</Typography>}
        </Alert>
        <Button
          variant="contained"
          onClick={() => dispatch(fetchBuses())}
          sx={{
            bgcolor: '#4338CA',
            borderRadius: 2.5,
            fontWeight: 700,
            textTransform: 'none',
            fontFamily: 'Cairo, Tajawal, sans-serif',
            '&:hover': { bgcolor: '#3730A3' },
          }}
        >
          إعادة المحاولة
        </Button>
      </Box>
    );
  }

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BusIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                إدارة أسطول الحافلات
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                إضافة وإدارة حافلات المدرسة والسائقين والمسارات
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            onClick={handleAddClick}
            startIcon={<AddIcon />}
            data-guide-id="guide-add-bus"
            sx={{
              bgcolor: '#fff',
              color: '#4338CA',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              boxShadow: 'none',
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
            }}
          >
            إضافة حافلة جديدة
          </Button>
        </MotionPaper>

        {/* Search and Filter Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="ابحث بالاسم، رقم اللوحة، السائق، أو المسار..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#6B7280' }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2.5,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                '& fieldset': { borderColor: '#E5E7EB' },
                '&:hover fieldset': { borderColor: '#4338CA' },
                '&.Mui-focused fieldset': { borderColor: '#4338CA' },
              },
            }}
          />
          {/* Add Filter Dropdowns here if needed (e.g., by status, type) */}
        </MotionPaper>

        {/* Buses Grid */}
        {filteredBuses.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              textAlign: 'center',
              p: 5,
              borderRadius: 3,
              bgcolor: '#fff',
              border: '1px dashed #E5E7EB',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#EEF2FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <BusIcon sx={{ color: '#4338CA', fontSize: 34 }} />
            </Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: '#1E1B4B', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}
            >
              {busList.length === 0 ? 'لم يتم إضافة أي حافلات بعد.' : 'لا توجد نتائج مطابقة لبحثك.'}
            </Typography>
            {busList.length === 0 && (
              <Button
                variant="outlined"
                onClick={handleAddClick}
                startIcon={<AddIcon />}
                sx={{
                  mt: 2,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  borderColor: '#4338CA',
                  color: '#4338CA',
                  '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
                }}
              >
                إضافة أول حافلة
              </Button>
            )}
          </Paper>
        ) : (
          <MotionGrid
            container
            spacing={3}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            {filteredBuses.map((bus, idx) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={bus.id}
                component={motion.div}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + idx * 0.07, duration: 0.5 }}
              >
                <StyledCard elevation={0} onClick={() => handleBusClick(bus.id)}>
                  <CardHeader>
                    <Avatar
                      sx={{
                        background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 100%)',
                        width: 46,
                        height: 46,
                      }}
                    >
                      <BusIcon sx={{ color: 'white' }} />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        noWrap
                        sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                      >
                        {bus.name || `حافلة ${bus.bus_number}` || 'حافلة غير مسماة'}
                      </Typography>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                      >
                        {bus.bus_number || 'لا يوجد رقم لوحة'}
                      </Typography>
                    </Box>
                  </CardHeader>

                  <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                    <Stack spacing={1.5}>
                      <InfoItem 
                        icon={<PersonIcon fontSize="small" />} 
                        label="السائق" 
                        value={
                          bus.driver_name && bus.driver_name !== 'غير محدد' 
                            ? bus.driver_name 
                            : bus.manager_name || 'غير محدد'
                        } 
                      />
                      <InfoItem icon={<RouteIcon fontSize="small" />} label="نوع الباص" value={bus.bus_type || 'غير محدد'} />
                      {/* Assuming student count might be available in the list view API response */}
                      {bus.student_count !== undefined && (
                         <InfoItem icon={<PeopleIcon fontSize="small" />} label="الطلاب" value={`${bus.student_count} طالب`} />
                      )}
                    </Stack>
                  </CardContent>

                  <CardActionBar>
                    <Stack useFlexGap direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="عرض التفاصيل">
                        <IconButton
                          size="small"
                          onClick={() => handleBusClick(bus.id)}
                          sx={{
                            color: '#4338CA',
                            bgcolor: '#EEF2FF',
                            '&:hover': { bgcolor: '#E0E7FF' },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="تعديل">
                        <IconButton
                          size="small"
                          onClick={(e) => handleEditClick(bus.id, e)}
                          sx={{
                            color: '#D97706',
                            bgcolor: '#FEF3C7',
                            '&:hover': { bgcolor: '#FDE68A' },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف">
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(bus.id, bus.name || bus.bus_number, e)}
                          sx={{
                            color: '#DC2626',
                            bgcolor: '#FEE2E2',
                            '&:hover': { bgcolor: '#FECACA' },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </CardActionBar>
                </StyledCard>
              </Grid>
            ))}
          </MotionGrid>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          dir="rtl"
          PaperProps={{
            sx: {
              borderRadius: 3,
              fontFamily: 'Cairo, Tajawal, sans-serif',
              boxShadow: '0 8px 32px rgba(67,56,202,0.18)',
            },
          }}
        >
          <DialogTitle
            sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
          >
            تأكيد الحذف
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              هل أنت متأكد أنك تريد حذف الحافلة "{selectedBusName}"؟ هذا الإجراء لا يمكن التراجع عنه.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              onClick={handleCancelDelete}
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#E5E7EB',
                color: '#6B7280',
                '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
              }}
            >
              إلغاء
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                bgcolor: '#DC2626',
                color: 'white',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
              }}
            >
              حذف
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Error Dialog */}
        <Dialog
          open={errorDialogOpen}
          onClose={handleCloseErrorDialog}
          dir="rtl"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              fontFamily: 'Cairo, Tajawal, sans-serif',
              boxShadow: '0 8px 32px rgba(67,56,202,0.18)',
            },
          }}
        >
          <DialogTitle
            sx={{ color: '#DC2626', fontWeight: 800, fontFamily: 'Cairo, Tajawal, sans-serif' }}
          >
            تعذر حذف الحافلة
          </DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mt: 1, mb: 2, borderRadius: 2.5, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              {errorMessage}
            </Alert>
            <Typography sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              يرجى حل المشكلة المذكورة أعلاه قبل محاولة حذف الحافلة مرة أخرى.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseErrorDialog}
              variant="contained"
              sx={{
                minWidth: 100,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                bgcolor: '#4338CA',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
              }}
            >
              موافق
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

// Helper component for info items in the card
const InfoItem = ({ icon, label, value }) => (
  <Stack useFlexGap direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
    {React.cloneElement(icon, { sx: { color: '#4338CA' } })}
    <Typography
      variant="body2"
      sx={{ flexShrink: 0, color: '#6B7280', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      noWrap
      sx={{ flexGrow: 1, textAlign: 'left', color: '#1E1B4B', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}
    >
      {value || <Typography component="span" variant="caption" color="text.disabled">غير محدد</Typography>}
    </Typography>
  </Stack>
);

export default BusesPage;